import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Modal,
} from 'components';
import { Margin } from 'styled-components-spacing';
import checkPermissions from '../../../helpers/checkPermissions';
import { DownloadIcon, File, ToUploadList } from '../ChangeRequest.styled';
import { iconList } from '../../../assets';

const ViewChangeRequest = ({
  profile,
  loading,
  loadDetails,
  approveChangeRequest,
  userPermissions,
  type,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    newStatus: null, // 'Approved' or 'Rejected'
  });
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const isIpma = type === 'ipma';
  const isCoo = checkPermissions({ roles: ['ipma_approve_change_request'] }, userPermissions || []);
  const canApproveReject = isIpma && isCoo && profile?.status === 'Pending';

  useEffect(() => {
    if (profile?.id) {
      loadDetails(profile.id, history);
    }
  }, [profile?.id, loadDetails, history]);

  const handleOpenConfirmModal = (newStatus) => {
    setConfirmModal({ isOpen: true, newStatus });
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ isOpen: false, newStatus: null });
  };

  const handleConfirmAction = async () => {
    if (approveChangeRequest && profile?.id && confirmModal.newStatus) {
      setIsActionInProgress(true);
      await approveChangeRequest(profile.id, { status: confirmModal.newStatus });
      if (profile?.id) {
        await loadDetails(profile.id, history);
      }
      setIsActionInProgress(false);
      handleCloseConfirmModal();
      history.goBack();
    }
  };

  if (loading) {
    return (
      <PageContent>
        <Container>
          <p>{t('global.loading')}</p>
        </Container>
      </PageContent>
    );
  }

  if (!profile || !profile.id) {
    return (
      <PageContent>
        <Container>
          <p>{t('global.noData')}</p>
        </Container>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Container>
        <Margin bottom="24">
          <Heading size="h5">
            {t('page.user.cis.changeRequests.viewChangeRequest')}
          </Heading>
        </Margin>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="status"
                label={t('page.user.cis.changeRequests.status')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.status || '-'}
                </div>
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="createdAt"
                label={t('page.user.cis.changeRequests.createdAt')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.createdAt && moment(profile.createdAt).format('DD/MM/YYYY HH:mm')}
                </div>
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="createdBy"
                label={t('page.user.cis.changeRequests.createdBy')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.createdBy || '-'}
                </div>
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 2 }}>
            <Margin bottom="20">
              <Field
                id="description"
                label={t('page.user.cis.changeRequests.description')}
              >
                <textarea readOnly rows={10}>
                  {profile.description || '-'}
                </textarea>
              </Field>
            </Margin>
          </Col>
        </Row>
        {profile.files && profile.files.length > 0 && (
          <Row>
            <Col size={{ xl: 1 }}>
              <Margin bottom="20">
                <Field
                  id="files"
                  label={t('page.user.cis.changeRequests.files')}
                >
                  <ToUploadList>
                      {profile.files.map((file, index) => (
                        <File key={`${file.fileName}-${index + 1}`}>
                          {file.fileUrl && (
                            <DownloadIcon
                              src={iconList.fileUpload}
                              title="Download file"
                              onClick={() => window.open(file.fileUrl, '_blank')}
                            />
                          )}
                          {file.fileName || `File ${index + 1}`}
                        </File>
                      ))}
                  </ToUploadList>
                </Field>
              </Margin>
            </Col>
          </Row>
        )}
        <Row>
          {canApproveReject && (
            <>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Button
                  onClick={() => handleOpenConfirmModal('Approved')}
                  type="button"
                  style={{ marginRight: '10px' }}
                >
                  {t('page.user.cis.changeRequests.approve')}
                </Button>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Button
                  onClick={() => handleOpenConfirmModal('Rejected')}
                  type="button"
                >
                  {t('page.user.cis.changeRequests.reject')}
                </Button>
              </Margin>
            </Col>
            </>
          )}
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Button secondary onClick={() => history.goBack()} type="button">
                {t('global.back')}
              </Button>
            </Margin>
          </Col>
        </Row>
        <Modal
          heading={
            confirmModal.newStatus === 'Approved'
              ? t('page.user.cis.changeRequests.confirmApproveTitle')
              : t('page.user.cis.changeRequests.confirmRejectTitle')
          }
          isOpen={confirmModal.isOpen}
          confirm={handleConfirmAction}
          cancel={handleCloseConfirmModal}
          onRequestClose={handleCloseConfirmModal}
          disabled={isActionInProgress}
          description={
            confirmModal.newStatus === 'Approved'
              ? t('page.user.cis.changeRequests.confirmApproveText')
              : t('page.user.cis.changeRequests.confirmRejectText')
          }
        />
      </Container>
    </PageContent>
  );
};

ViewChangeRequest.propTypes = {
  profile: PropTypes.object,
  loading: PropTypes.bool,
  loadDetails: PropTypes.func.isRequired,
  approveChangeRequest: PropTypes.func,
  userPermissions: PropTypes.array,
  type: PropTypes.string,
};

ViewChangeRequest.defaultProps = {
  profile: {},
  loading: false,
  approveChangeRequest: null,
  userPermissions: [],
  type: 'ipma',
};

export default ViewChangeRequest;
